import { Module } from 'lib/vendor/terrific/Module';
import { Hook } from 'lib/vendor/terrific/Sandbox';
import ContactStep from './ContactStep';
import ContactTopic from './ContactTopic';
import ContactResult from './ContactResult';
import { trackSelectionChange } from './helpers';
import { IHash, ITitle } from './models';

import { createAnimation, AnimationHandle } from 'lib/util/createAnimation';

const defaultHashManipulator = {
	hashSetter(hash : string) { window.location.hash = hash; },
	hashGetter() { return window.location.hash; },
};
const defaultTitleManipulator = {
	titleSetter(title : string) { document.title = title; },
	titleGetter() { return document.title; },
};

export default class SDXContactWizard extends Module {
	private steps! : ContactStep[];
	private scrollAnimation! : AnimationHandle<number>;
	private currentHash? : string;
	private defaultTitle! : string;
	private hashManipulator! : IHash;
	private titleManipulator! : ITitle;

	public on(callback : Hook) {
		this.setHashManipulator(defaultHashManipulator);
		this.setTitleManipulator(defaultTitleManipulator);
		this.steps = [];

		callback();
	}

	public after() {
		// Find steps and topics
		this.initializeSteps();
		this.initializeDom();
		this.loadStateFromHash();
		this.hashChanged();
		this.updateTopicClasses();
	}

	public topicSelected(topic : ContactTopic) {
		const hash = (topic && topic.topicName) || undefined;
		trackSelectionChange(topic);
		this.updateTitle(topic);
		this.setHash(hash);
		this.scrollToTopic(topic);
		this.fire('topicSelected', topic);
	}

	public setHashManipulator(hashManipulator : IHash) {
		this.hashManipulator = hashManipulator;
	}

	public setTitleManipulator(titleManipulator : ITitle) {
		this.titleManipulator = titleManipulator;
	}

	private initializeSteps() {
		const $steps = this.$ctx.find('.steps > .step');
		let previousStep : ContactStep;

		$steps.each((_id, step) => {
			const $step = $(step);
			const contactStep = new ContactStep($step, this, previousStep);

			this.steps.push(contactStep);

			// Handle topics
			const $topics = $step.find('.topics > .topic');
			$topics.each((_idx, topic) => {
				const $topic = $(topic);
				const contactTopic = new ContactTopic($topic);
				contactStep.addTopic(contactTopic);
			});

			const $results = $step.find('.results > .result');
			$results.each((_idx, result) => {
				const $result = $(result);
				const contactResult = new ContactResult($result);
				contactStep.addResult(contactResult);
			});

			previousStep = contactStep;
		});
	}

	private initializeDom() {
		const $window = $(window);
		// Bind hash change handlers
		$window.on('hashchange', this.hashChanged.bind(this));
		// Store initial document title
		this.defaultTitle = this.titleManipulator.titleGetter();
		// Create animator
		this.scrollAnimation = createAnimation({
			startValueCalculator() {
				return document.documentElement.scrollTop;
			},
			animator(_time : number, scrollTop : number) {
				window.scroll(undefined as any, scrollTop);
			},
			easing: 'swing',
		});
	}

	private scrollToTopic(topic : ContactTopic) {
		if (!topic) {
			return;
		}
		const step = topic.step;
		if (!step) {
			return;
		}

		const pos = step.getContext().offset();
		if (!pos) {
			return;
		}

		const $correctionElement = $(document.body).find('.modHeader .scsh-lvl2-container');
		const correction = $correctionElement.length > 0 ? $correctionElement.height()! - 30 : 0;

		this.scrollAnimation.start({
			endValue: pos.top - correction,
		});
	}

	private setHash(hash? : string) {
		if (this.currentHash === hash) {
			return;
		}
		this.currentHash = hash;
		this.hashManipulator.hashSetter(hash || '');
	}

	private getHash() {
		let hash = this.hashManipulator.hashGetter() || '';
		if (hash.charAt(0) === '#') {
			hash = hash.substring(1);
		}
		if (hash === this.currentHash) {
			return undefined;
		}

		return hash;
	}

	private loadStateFromHash(hash? : string) {
		if (!hash || !this.steps || !this.steps.length) {
			return;
		}

		let topic : ContactTopic | null = null;

		// Find topic by name
		for (const step of this.steps) {
			topic = step.namedTopic(hash) as ContactTopic;
			if (topic) {
				break;
			}
		}

		if (topic) {
			this.currentHash = hash;
			this.steps[0].selectTopic(topic);
		} else if (!hash) {
			this.steps[0].deselect();
		}
	}

	private hashChanged() {
		const hash = this.getHash();
		this.loadStateFromHash(hash);
	}

	private updateTitle(topic : ContactTopic) {
		const titles = [];

		while (topic) {
			titles.unshift(topic.getTitle());
			topic = topic.parent as ContactTopic;
		}

		let newTitle = this.defaultTitle;
		if (titles.length) {
			newTitle = `${titles.join(': ')} – ${newTitle}`;
		}
		this.titleManipulator.titleSetter(newTitle);
	}

	private updateTopicClasses() {
		this.steps.forEach((step : ContactStep) => {
			step.getTopics().forEach((topic : ContactTopic) => {
				if (!topic.parent) {
					const children = topic.getChildren();
					children.forEach((child : any, idx : number) => {
						if(!(idx % 3 - 1)) {
							(child as ContactTopic).getContext().addClass('margin-side');
						}
					});
				}
			});
		});
	}
}



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/contact/Tc.Module.SDXContactWizard.ts